import { create } from 'zustand'
import i18next from 'i18next'
import PatientService from '@/services/PatientService'
import notifyApiError from '@/utils/notifyApiError'
import { onboardingPolicy } from '@/regions/onboarding'
import { region } from '@/config/locales'
import { RESPONSE } from '@/utils/constants'
import { useMessageStore } from '@/stores/messageStore'
import { useAuthStore } from '@/stores/authStore'
import type { PatientStore, PatientInfo } from '@/types'

export const usePatientStore = create<PatientStore>((set, get) => {
  i18next.on('languageChanged', () => {
    const { patientInfo } = get()
    if (patientInfo) {
      set({ actionList: onboardingPolicy.buildActions(patientInfo, i18next.t.bind(i18next)) })
    }
  })

  return {
    patientInfo: null,
    messages: 0,
    referrals: [],
    identificationSession: [],
    actionList: [],
    isLoading: false,
    loadFailed: false,

    fetchPatientInfo: async (): Promise<PatientInfo | null> => {
      set({ isLoading: true, loadFailed: false })

      const t = i18next.t.bind(i18next)

      try {
        const res = await PatientService.getInfo()
        const patient = (res?.data ?? {}) as PatientInfo
        const threads = patient.internal_message_threads ?? []
        const unread = threads.filter((thread) => thread.count_unread > 0).length

        // Geen user-blok betekent status onbekend, niet "niet onboarded".
        const onBoarding = patient?.user?.onBoardingComplete
        const statusKnown = typeof onBoarding === 'boolean'

        set({
          patientInfo: patient,
          messages: unread,
          referrals: patient.referrals ?? [],
          identificationSession: patient.identificationSession ?? [],
          actionList: onboardingPolicy.buildActions(patient, t),
          isLoading: false,
          loadFailed: !statusKnown,
        })

        // TODO(UK): tijdelijk, UK heeft nog geen onboarding en de backend zet onBoardingComplete daar niet.
        const onboarded = region === 'uk' || onBoarding
        useAuthStore.getState().setOnboarding(statusKnown ? onboarded : null)

        // Een 200 zonder user-blok laat de app in "status onbekend" achter en was
        // tot nu toe volledig stil. getInfo is de levenslijn van de portal, dus
        // altijd rapporteren.
        if (!statusKnown) {
          notifyApiError(new Error('getInfo returned no user block'), {
            t,
            forceReport: true,
            tags: { source: 'getInfo', reason: 'no-user-block' },
          })
        }

        return patient
      } catch (err: unknown) {
        set({ isLoading: false, loadFailed: true })

        if ((err as { status?: number })?.status === RESPONSE.RESP_UNAUTHORIZED) {
          useAuthStore.getState().logout()
          return null
        }

        // forceReport: netwerkfouten zijn standaard stil, en juist een getInfo die
        // de server nooit haalt is wat we moeten zien -- zonder dit staat er noch
        // in de audit-log noch in AppSignal iets.
        notifyApiError(err, {
          t,
          showMessage: useMessageStore.getState().showMessage,
          forceReport: true,
          tags: { source: 'getInfo' },
        })
        return null
      }
    },

    // Kept as a named alias so call sites read intent ("refresh"); auth rides in
    // the cookie, so there is nothing to thread through.
    refreshPatientInfo: async (): Promise<PatientInfo | null> => get().fetchPatientInfo(),

    reset: () => {
      set({
        patientInfo: null,
        messages: 0,
        referrals: [],
        identificationSession: [],
        actionList: [],
        isLoading: false,
        loadFailed: false,
      })
    },
  }
})
